import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Avatar,
  useTheme,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Delete,
  Stars,
  EmojiEvents,
  Lightbulb,
  Flag,
  Favorite,
  AutoAwesome,
  ArrowBack
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import {
  getRecognitions,
  createRecognition,
  deleteRecognition,
  Recognition
} from '@/services/DashboardAnalyticsService';
import { getAllEmployees } from '@/services/HrService/EmployeesService';

interface Employee {
  id: string;
  name: string;
  email: string;
}

const recognitionTypes = [
  { value: 'star_performer', label: 'Star Performer', icon: Stars, color: '#FFD700' },
  { value: 'team_player', label: 'Team Player', icon: Favorite, color: '#E91E63' },
  { value: 'innovation', label: 'Innovation', icon: Lightbulb, color: '#FF9800' },
  { value: 'milestone', label: 'Milestone', icon: Flag, color: '#4CAF50' },
  { value: 'customer_champion', label: 'Customer Champion', icon: EmojiEvents, color: '#2196F3' },
  { value: 'custom', label: 'Custom', icon: AutoAwesome, color: '#9C27B0' }
];

const getTypeConfig = (type: string) => {
  return recognitionTypes.find(t => t.value === type) || recognitionTypes[5];
};

type RecognitionType = 'star_performer' | 'team_player' | 'innovation' | 'milestone' | 'customer_champion' | 'custom';

interface RecognitionFormData {
  employee_id: string;
  recognition_type: RecognitionType;
  title: string;
  description: string;
  is_featured: boolean;
}

const initialFormData: RecognitionFormData = {
  employee_id: '',
  recognition_type: 'star_performer',
  title: '',
  description: '',
  is_featured: false
};

const RecognitionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [recognitions, setRecognitions] = useState<Recognition[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<RecognitionFormData>(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recognitionToDelete, setRecognitionToDelete] = useState<Recognition | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

  // Handle ?create=true query param to auto-open dialog
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setDialogOpen(true);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const fetchRecognitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getRecognitions(1, 50);
      if (response.success && response.data) {
        setRecognitions(response.data);
      } else {
        setError(response.error?.message || 'Failed to load recognitions');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load recognitions');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await getAllEmployees(1, 100);
      if (response.success && response.data) {
        setEmployees(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  useEffect(() => {
    fetchRecognitions();
    fetchEmployees();
  }, []);

  const handleOpenDialog = () => {
    setFormData(initialFormData);
    setSelectedEmployee(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData(initialFormData);
    setSelectedEmployee(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === 'recognition_type') {
      setFormData(prev => ({ ...prev, recognition_type: value as RecognitionType }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleEmployeeChange = (_: any, value: Employee | null) => {
    setSelectedEmployee(value);
    setFormData(prev => ({ ...prev, employee_id: value?.id || '' }));
  };

  const handleSubmit = async () => {
    if (!formData.employee_id) {
      setError('Please select an employee');
      return;
    }
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await createRecognition(formData);

      if (response.success) {
        handleCloseDialog();
        fetchRecognitions();
      } else {
        setError(response.error?.message || 'Failed to give recognition');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to give recognition');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (recognition: Recognition) => {
    setRecognitionToDelete(recognition);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recognitionToDelete) return;

    try {
      const response = await deleteRecognition(recognitionToDelete.id);
      if (response.success) {
        setDeleteConfirmOpen(false);
        setRecognitionToDelete(null);
        fetchRecognitions();
      } else {
        setError(response.error?.message || 'Failed to delete recognition');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete recognition');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Box sx={{ py: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/dashboard')}>
                <ArrowBack />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Employee Recognitions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Celebrate achievements and recognize team members
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenDialog}
            >
              Give Recognition
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : recognitions.length === 0 ? (
            <Card sx={{
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
              textAlign: 'center',
              py: 8
            }}>
              <Stars sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No recognitions yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start celebrating your team's achievements
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={handleOpenDialog}>
                Give Recognition
              </Button>
            </Card>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3
            }}>
              {recognitions.map((recognition) => {
                const typeConfig = getTypeConfig(recognition.recognition_type);
                const TypeIcon = typeConfig.icon;

                return (
                  <Card key={recognition.id} sx={{
                    height: '100%',
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
                    position: 'relative',
                    '&:hover': {
                      borderColor: typeConfig.color,
                      boxShadow: theme.shadows[4]
                    }
                  }}>
                    {recognition.is_featured && (
                      <Chip
                        label="Featured"
                        size="small"
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8
                        }}
                      />
                    )}
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{
                          bgcolor: `${typeConfig.color}20`,
                          color: typeConfig.color,
                          width: 48,
                          height: 48
                        }}>
                          <TypeIcon />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {recognition.employee_name}
                          </Typography>
                          <Chip
                            label={typeConfig.label}
                            size="small"
                            sx={{
                              bgcolor: `${typeConfig.color}20`,
                              color: typeConfig.color,
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                        {recognition.title}
                      </Typography>

                      {recognition.description && (
                        <Typography variant="body2" color="text.secondary" sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {recognition.description}
                        </Typography>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            By {recognition.recognized_by_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(recognition.created_at)}
                          </Typography>
                        </Box>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(recognition)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}

          {/* Give Recognition Dialog */}
          <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle>
              Give Recognition
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(option) => `${option.name} (${option.email})`}
                  value={selectedEmployee}
                  onChange={handleEmployeeChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Employee"
                      required
                      placeholder="Search by name or email"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar sx={{ width: 32, height: 32, mr: 1, fontSize: '0.875rem' }}>
                        {option.name.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
                <FormControl fullWidth>
                  <InputLabel>Recognition Type</InputLabel>
                  <Select
                    value={formData.recognition_type}
                    label="Recognition Type"
                    onChange={(e) => handleSelectChange('recognition_type', e.target.value)}
                  >
                    {recognitionTypes.map((type) => {
                      const TypeIcon = type.icon;
                      return (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TypeIcon sx={{ color: type.color, fontSize: 20 }} />
                            {type.label}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <TextField
                  name="title"
                  label="Title"
                  value={formData.title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  placeholder="e.g., Outstanding Q4 Performance"
                />
                <TextField
                  name="description"
                  label="Message"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Share what makes this person deserving of recognition..."
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_featured}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    />
                  }
                  label="Feature on Wall of Fame"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
                startIcon={submitting ? <CircularProgress size={20} /> : <Stars />}
              >
                {submitting ? 'Sending...' : 'Give Recognition'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Delete Recognition</DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to delete the recognition for "{recognitionToDelete?.employee_name}"? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeleteConfirm}>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>
    </Layout>
  );
};

export default RecognitionsPage;
